import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { AppState } from "src/app/core/app.state";
import { ProductsService } from "../services/produits.services";
import * as productsActions from "./produits.actions"
import { catchError, finalize, map, mergeMap, of, pipe, switchMap, tap } from "rxjs";
import { APIResponse } from "src/app/core/models/api-response";
import { IProduct, IProductCatalogsData, IProductCompany, IProductInventoryDetails, IProductOrdersKPI } from "../models/products.models";
import { CommonService } from "src/app/shared/common.service";
import { AlertEnums } from "src/app/shared/enums/shared.enums";
import { TranslateService } from "@ngx-translate/core";
import { DEFAULT_PAGE_SIZE } from "src/app/config/global.config";
import { FeatureFlagService } from "src/app/core/services/feature-flag.service";

@Injectable()
export class ProductsEffects {
  constructor(
    private actions$: Actions,
    public store: Store<AppState>,
    private productsServices: ProductsService,
    private commonService: CommonService,
    private translateService: TranslateService,
    private featureFlagService: FeatureFlagService
  ) { }

    // fetch all Products
    FetchAllProductsEffect$ = createEffect(() =>
      this.actions$.pipe(
        ofType(productsActions.FetchAllProducts),
        tap(() => {
          this.store.dispatch(productsActions.SetProductLoader({ isLoading: true }));
        }),
        switchMap(() =>
          this.productsServices.listProducts().pipe(
            map((res: APIResponse<IProduct>) => productsActions.FetchAllProductsSuccess({ products: res })),
            catchError(() => of(
              productsActions.FetchAllProductsError()
            )),
            finalize(() => {
              this.store.dispatch(productsActions.SetProductLoader({ isLoading: false }));
            })
          )
        )
      )
    );

  // fetch Products
  FetchProductsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(productsActions.FetchProducts),
      tap(() => {
        this.store.dispatch(productsActions.SetProductLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.productsServices.listAllProducts(action.page, action.filter, action.key, action.sort_by, action.sort_mode, action.priority).pipe(
          map((res: APIResponse<IProduct>) => productsActions.FetchProductsSuccess({ products: res })),
          catchError(() => of(
            productsActions.FetchProductsError()
          )),
           finalize(() => {
            this.store.dispatch(productsActions.SetProductLoader({ isLoading: false }));
          })
          )
        )
      ),
  );

  // get Product By Id
  GetProductByIdEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(productsActions.GetProductById),
      tap(() => {
        this.store.dispatch(productsActions.SetProductLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.productsServices.getProductById(action.id!).pipe(
          map((res: APIResponse<IProduct>) => productsActions.GetProductByIdSuccess({ product: res.data[0] })),
          catchError(() =>
            of(
              productsActions.GetProductByIdError()
          )),
          finalize(() => {
            this.store.dispatch(productsActions.SetProductLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // get Product Inventory Details
  GetProductInventoryDetailsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(productsActions.GetProductInventoryDetails),
      tap(() => {
        this.store.dispatch(productsActions.SetProductLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.productsServices.getProductInventoryDetails(action.id!).pipe(
          map((res: APIResponse<IProductInventoryDetails>) => {
            return productsActions.GetProductInventoryDetailsSuccess({ inventoryDetails: res });
          }),
          catchError(() =>
            of(
              productsActions.GetProductInventoryDetailsError()
          )),
          finalize(() => {
            this.store.dispatch(productsActions.SetProductLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // get Product Companies
  GetProductCompaniesEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(productsActions.GetProductCompanies),
      tap(() => {
        this.store.dispatch(productsActions.SetProductLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.productsServices.getProductCompanies(action.id!).pipe(
          map((res: APIResponse<IProductCompany>) => productsActions.GetProductCompaniesSuccess({ productCompanies: res })),
          catchError(() =>
            of(
              productsActions.GetProductCompaniesError()
          )),
          finalize(() => {
            this.store.dispatch(productsActions.SetProductLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // get Product Orders KPI
  GetProductOrdersKPIEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(productsActions.GetProductOrdersKPI),
      tap(() => {
        this.store.dispatch(productsActions.SetProductLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.productsServices.getProductOrdersKPI(action.id!).pipe(
          map((res: APIResponse<IProductOrdersKPI>) => productsActions.GetProductOrdersKPISuccess({ productOrdersKPI: res })),
          catchError(() =>
            of(
              productsActions.GetProductOrdersKPIError()
            )),
          finalize(() => {
            this.store.dispatch(productsActions.SetProductLoader({ isLoading: false }));
          })
        )
      )
    )
  );
  // add Product
  AddProductEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(productsActions.AddProduct),
      tap(() => {
        this.store.dispatch(productsActions.SetProductLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.productsServices.addProduct(action.product).pipe(
          map((res: APIResponse<IProduct>) => productsActions.AddProductSuccess({ product: res.data[0] })),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(`alerts.${AlertEnums.productAddSuccess}`),
              'SUCCESS'
            );
          }),
          catchError(() =>
            of(
              productsActions.AddProductError()
            )
            // .pipe(
            //   tap(() => {
            //     this.commonService.showToast(
            //       this.translateService.instant(`alerts.${AlertEnums.productEditError}`),
            //       'ERROR'
            //     )
            //   }
            //   )
            // )
          ),
          finalize(() => {
            this.store.dispatch(productsActions.SetProductLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // add Product with variant
  AddProductWithVariantsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(productsActions.AddProductWithVariants),
      tap(() => {
        this.store.dispatch(productsActions.SetProductLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.productsServices.addProductWithVariants(action.product).pipe(
          map((res: APIResponse<IProduct>) => productsActions.AddProductWithVariantsSuccess({ product: res.data[0] })),
          catchError(() =>
            of(
              productsActions.AddProductWithVariantsError()
          )),
          finalize(() => {
            this.store.dispatch(productsActions.SetProductLoader({ isLoading: false }));
          })
        )
      ),
    )
  );

    // update variants of Product
    UpdateVariantsEffect$ = createEffect(() =>
      this.actions$.pipe(
        ofType(productsActions.UpdateVariantsProduct),
        tap(() => {
          this.store.dispatch(productsActions.SetProductLoader({ isLoading: true }));
        }),
        switchMap((action) =>
          this.productsServices.updateVariantsProduct(action.id, action.product).pipe(
            map((res: APIResponse<IProduct>) => productsActions.AddProductWithVariantsSuccess({ product: res.data[0] })),
            tap(() => {
              this.store.dispatch(productsActions.FetchProducts({ page: { number: 1, size: DEFAULT_PAGE_SIZE } }));
              this.store.dispatch(productsActions.GetProductById({ id: action.id }));
              // this.commonService.showToast(
              //   AlertEnums.addSuccess,
              //   'SUCCESS'
              // );
            }),
            catchError(() =>
              of(
                productsActions.AddProductWithVariantsError()
              )),
            finalize(() => {
              this.store.dispatch(productsActions.SetProductLoader({ isLoading: false }));
            })
          )
        )
      )
    );

  // improve varinats product
  ImproveVariantsProductEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(productsActions.ImproveVariantsProduct),
      tap(() => {
        this.store.dispatch(productsActions.SetVariantsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.productsServices.improveVariants(action.variants).pipe(
          map((res: APIResponse<IProduct>) => productsActions.ImproveVariantsProductSuccess({ product: res.data[0] })),
          tap(() => {
            if(action.product){
              this.store.dispatch(productsActions.UpdateVariantsProduct({ id: action.idProduct!, product: action.product }));
            }
            this.commonService.showToast(
              this.translateService.instant(`alerts.${AlertEnums.productAddSuccess}`),
              'SUCCESS'
            );
          }),
          catchError(() =>
            of(
              productsActions.ImproveVariantsProductError()
            )
            // .pipe(
            //   tap(() => {
            //     this.commonService.showToast(
            //       this.translateService.instant(`alerts.${AlertEnums.productEditError}`),
            //       'ERROR'
            //     )
            //   }
            //   )
            // )
          ),
          finalize(() => {
            this.store.dispatch(productsActions.SetVariantsLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // update Product
  UpdateProductEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(productsActions.UpdateProduct),
      tap(() => {
        this.store.dispatch(productsActions.SetProductLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.productsServices.updateProduct(action.product, action.id).pipe(
          map((res: APIResponse<IProduct>) => productsActions.UpdateProductSuccess({ product: res.data[0] })),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(`alerts.${AlertEnums.productUpdateSuccess}`),
              'SUCCESS'
            );
          }),
          catchError(() =>
            of(
              productsActions.UpdateProductError()
            )
            // .pipe(
            //   tap(() => {
            //     this.commonService.showToast(
            //       this.translateService.instant(`alerts.${AlertEnums.productEditError}`),
            //       'ERROR'
            //     )
            //   }
            //   )
            // )
          ),
          finalize(() => {
            this.store.dispatch(productsActions.SetProductLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // delete Product
  DeleteProductEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(productsActions.DeleteProduct),
      tap(() => {
        this.store.dispatch(productsActions.SetProductLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.productsServices.deleteProduct(action.id).pipe(
          map(() => productsActions.DeleteProductSuccess()),
          tap(() => {
            // this.store.dispatch(productsActions.FetchProducts({ page: { number: 1, size: DEFAULT_PAGE_SIZE } }));
            this.commonService.showToast(
              this.translateService.instant(`alerts.${AlertEnums.productDeleteSuccess}`),
              'SUCCESS'
            );
          }),
          catchError(() =>
            of(
              productsActions.DeleteProductError()
            )
            // .pipe(
            //   tap(() => {
            //     this.commonService.showToast(
            //       this.translateService.instant(`alerts.${AlertEnums.productEditError}`),
            //       'ERROR'
            //     )
            //   }
            //   )
            // )
          ),
          finalize(() => {
            this.store.dispatch(productsActions.SetProductLoader({ isLoading: false }));
          })
        )
      ),
    )
  );


  // delete multiple products
  DeleteProductsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(productsActions.DeleteProducts),
      tap(() => {
        this.store.dispatch(productsActions.SetProductLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.productsServices.deleteProducts(action.product_ids).pipe(
          map(() => productsActions.DeleteProductsSuccess()),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(`alerts.${AlertEnums.productDeleteSuccess}`),
              'SUCCESS'
            );
          }),
          catchError(() =>
            of(
              productsActions.DeleteProductsError()
            )
          ),
          finalize(() => {
            this.store.dispatch(productsActions.SetProductLoader({ isLoading: false }));
          })
        )
      ),
    ),
  );
// export
exportProducts$ = createEffect(() =>
  this.actions$.pipe(
    ofType(productsActions.exportProducts),
    tap(() => {
      this.store.dispatch(productsActions.SetProductLoader({ isLoading: true }));
    }),
    mergeMap((action) =>
      this.productsServices.exportProducts(action.filter, action.key).pipe(
        map((file) => {
          const fileURL = window.URL.createObjectURL(file);
          const link = document.createElement('a');
          link.href = fileURL;
          link.download = `Products-${new Date().toLocaleString()}.xlsx`;
          link.click();
          return productsActions.exportProductsSuccess({ file });
        }),
        catchError((error) =>
          of(productsActions.exportProductsFailure({ error }))
        ),
        finalize(() => {
          this.store.dispatch(productsActions.SetProductLoader({ isLoading: false }));
        })
      )
    )
    )
  );

  // fetch products by search
  FetchProductsBySearchEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(productsActions.FetchProductsBySearch),
      tap((action) => {
        if (!action.skipGlobalLoader) {
          this.store.dispatch(productsActions.SetProductLoader({ isLoading: true }));
        }
      }),
      switchMap((action) => this.productsServices.fetchProductsBySearch(
        action.page,
        action.filter,
        action.keyword!,
        action.priority,
        this.featureFlagService.loyaltyEnabled ? (action.includeDeals ?? true) : false
      ).pipe(
        map((res: APIResponse<IProduct>) => productsActions.FetchProductsBySearchSuccess({ products: res, append: action.append })),
        catchError((error) =>
          of(productsActions.FetchProductsBySearchError({ error }))
        ),
        finalize(() => {
          if (!action.skipGlobalLoader) {
            this.store.dispatch(productsActions.SetProductLoader({ isLoading: false }));
          }
        })
      ))
    )
  );

  // get Product Catalogs
  GetProductCatalogsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(productsActions.GetProductCatalogs),
      tap(() => {
        this.store.dispatch(productsActions.SetProductLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.productsServices.getProductCatalogs(action.id!).pipe(
          map((res: APIResponse<IProductCatalogsData>) => productsActions.GetProductCatalogsSuccess({ productCatalogs: res })),
          catchError(() =>
            of(
              productsActions.GetProductCatalogsError()
          )),
          finalize(() => {
            this.store.dispatch(productsActions.SetProductLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // update Product Handling Units
  UpdateProductHandlingUnitsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(productsActions.UpdateProductHandlingUnits),
      tap(() => {
        this.store.dispatch(productsActions.SetProductLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.productsServices.updateProductHandlingUnits(
          action.productVariantId,
          action.handlingUnits,
          action.mergeExisting ?? false
        ).pipe(
          map(() => {
            this.commonService.showToast(
              this.translateService.instant('inventory.inventory_tab.handling_unit.update_success'),
              'SUCCESS'
            );
            return productsActions.UpdateProductHandlingUnitsSuccess();
          }),
          catchError((error) => {
            return of(productsActions.UpdateProductHandlingUnitsError());
          }),
          finalize(() => {
            this.store.dispatch(productsActions.SetProductLoader({ isLoading: false }));
          })
        )
      )
    )
  );

}
